import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogActions, DialogContent, DialogTitle,
    Button, Box, Alert, MenuItem, FormControl, InputLabel, Select, TextField, Tabs, Tab
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { UserService } from '../api/services/userService';

export default function AssignAdminModal({ open, onClose, onSave, existingAdmins = [] }) {
    const [tab, setTab] = useState(0);
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            setTab(0);
            setSelectedUserId('');
            setEmail('');
            setError(null);
            fetchUsers();
        }
    }, [open]);

    const fetchUsers = async () => {
        try {
            const data = await UserService.getAll({ noPagination: true });
            let allUsers = data.data || data || [];
            
            // Filter out users that are already assigned
            const existingIds = new Set(existingAdmins.map(admin => admin.id || admin._id));
            const availableUsers = allUsers.filter(user => !existingIds.has(user.id || user._id));
            
            setUsers(availableUsers);
        } catch (err) {
            console.error("Failed to fetch users", err);
        }
    };

    const handleSubmit = async () => {
        if (tab === 0 && !selectedUserId) {
            setError('Please select a user to assign.');
            return;
        }
        if (tab === 1 && !email.trim()) {
            setError('Please enter an email address to invite.');
            return;
        }

        setIsSubmitting(true);
        setError(null);
        try {
            if (tab === 0) {
                await onSave({ action: 'Assign', userId: selectedUserId });
            } else {
                await onSave({ action: 'Invite', email: email.trim() });
            }
            onClose();
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to assign/invite admin');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason === 'backdropClick' || isSubmitting) return;
                onClose();
            }}
            PaperProps={{ sx: { borderRadius: 3, padding: 1, minWidth: 440 } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                <PersonAddIcon color="primary" /> {tab === 0 ? 'Assign Admin' : 'Invite Admin'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ borderBottom: 1, borderColor: '#eaecf0', mb: 2 }}>
                    <Tabs value={tab} onChange={(e, val) => setTab(val)}>
                        <Tab label="Assign Existing User" />
                        <Tab label="Invite New User" />
                    </Tabs>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                    {error && (
                        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
                    )}

                    {tab === 0 ? (
                        <FormControl fullWidth size="small">
                            <InputLabel>Select User</InputLabel>
                            <Select
                                value={selectedUserId}
                                label="Select User"
                                onChange={(e) => setSelectedUserId(e.target.value)}
                                disabled={isSubmitting}
                            >
                                {users.map(u => (
                                    <MenuItem key={u.id} value={u.id}>{u.name} ({u.email})</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (
                        <TextField
                            fullWidth
                            label="Email Address"
                            type="email"
                            placeholder="e.g. admin@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={isSubmitting}
                            size="small"
                        />
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ padding: '0 24px 16px 24px' }}>
                <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }} disabled={isSubmitting}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    sx={{ borderRadius: 2 }}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Processing...' : (tab === 0 ? 'Assign' : 'Invite')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
